<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div
    class="color-picker"
    :class="{ 'inline-mode': inline, disabled: disabled }"
  >
    <!-- Popup mode: Input field and preview -->
    <!-- <div v-if="!inline" class="input-wrapper">
      <div
        class="color-preview"
        :style="{ backgroundColor: color.hex }"
        @click="togglePicker"
      ></div>
    </div> -->

    <!-- Color picker panel -->
    <div
      v-if="inline || showPicker"
      class="picker-panel"
      :class="{ 'popup-panel': !inline }"
      :style="popupPosition"
    >
      <!-- Saturation-Value Panel -->
      <div
        class="sv-panel"
        @mousedown="startSVDrag"
        ref="svPanel"
        :style="svPanelStyle"
      >
        <div
          class="sv-selector"
          :style="{ left: svPosition.x + '%', top: svPosition.y + '%' }"
        ></div>
      </div>

      <!-- Hue Slider -->
      <div class="hue-slider" @mousedown="startHueDrag" ref="hueSlider">
        <div class="hue-selector" :style="{ left: huePosition + '%' }"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    modelValue: {
      type: String,
      default: "#FFFFFF",
    },
    inline: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: "bottom",
    },
    style: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      showPicker: false,
      color: {
        hex: "#FFFFFF",
        rgb: { r: 255, g: 255, b: 255 },
        hsl: { h: 0, s: 0, l: 1 },
      },
      svPosition: { x: 0, y: 100 },
      huePosition: 0,
      isDraggingSV: false,
      isDraggingHue: false,
    };
  },
  computed: {
    popupPosition() {
      if (this.inline) return {};
      return this.position === "bottom"
        ? { top: "100%", right: "16px" }
        : { bottom: "100%", right: "16px" };
    },
    hueColor() {
      const rgb = this.hslToRgb({ h: this.color.hsl.h, s: 1, l: 0.5 });
      return this.rgbToHex(rgb);
    },
    svPanelStyle() {
      return {
        background: `
        linear-gradient(to right, #ffffff, ${this.hueColor}),
        linear-gradient(to top, #000000, transparent)
      `,
      };
    },
  },
  watch: {
    modelValue: {
      handler(newVal) {
        try {
          this.updateColor(newVal);
          this.emitColor();
        } catch (error) {
          console.error("Error updating color:", error, "Input:", newVal);
          this.updateColor("#FFFFFF");
          this.emitColor();
        }
      },
      immediate: true,
    },
    showPicker(newVal) {
      if (!newVal && !this.inline) {
        this.emitColor();
      }
    },
  },
  methods: {
    updateColor(value) {
      let hex, rgb, hsl;

      if (!value || value === "" || typeof value !== "string") {
        hex = "#FFFFFF";
        rgb = { r: 255, g: 255, b: 255 };
        hsl = { h: 0, s: 0, l: 1 };
      } else {
        hex = value;
        rgb = this.hexToRgb(hex);
        if (!rgb) {
          hex = "#FFFFFF";
          rgb = { r: 255, g: 255, b: 255 };
          hsl = { h: 0, s: 0, l: 1 };
        } else {
          hsl = this.rgbToHsl(rgb);
        }
      }

      this.color = { hex, rgb, hsl };
      this.svPosition = { x: hsl.s * 100, y: hsl.l * 100 };
      this.huePosition = (hsl.h / 360) * 100;
    },
    emitColor() {
      console.log("Emitting update:modelValue with:", this.color.hex);
      this.$emit("update:modelValue", this.color.hex);
    },
    // Color Conversions
    hexToRgb(hex) {
      if (!hex || typeof hex !== "string") return null;
      hex = hex.replace("#", "");
      if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(hex)) return null;
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((c) => c + c)
          .join("");
      }
      return {
        r: parseInt(hex.substring(0, 2), 16),
        g: parseInt(hex.substring(2, 4), 16),
        b: parseInt(hex.substring(4, 6), 16),
      };
    },
    rgbToHex(rgb) {
      const toHex = (x) => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      };
      return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
    },
    rgbToHsl(rgb) {
      let { r, g, b } = rgb;
      r /= 255;
      g /= 255;
      b /= 255;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h,
        s,
        l = (max + min) / 2;
      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r:
            h = (g - b) / d + (g < b ? 6 : 0);
            break;
          case g:
            h = (b - r) / d + 2;
            break;
          case b:
            h = (r - g) / d + 4;
            break;
        }
        h *= 60;
      }
      return { h, s, l };
    },
    hslToRgb(hsl) {
      let { h, s, l } = hsl;
      h /= 360;
      let r, g, b;
      if (s === 0) {
        r = g = b = l;
      } else {
        const hue2rgb = (p, q, t) => {
          if (t < 0) t += 1;
          if (t > 1) t -= 1;
          if (t < 1 / 6) return p + (q - p) * 6 * t;
          if (t < 1 / 2) return q;
          if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
          return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
      }
      return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255),
      };
    },
    hsvToHex(h, s, v) {
      const hsl = { h, s, l: v };
      const rgb = this.hslToRgb(hsl);
      return this.rgbToHex(rgb);
    },
    // Drag Handlers
    startSVDrag(event) {
      if (this.disabled) return;
      this.isDraggingSV = true;
      this.updateSV(event);
      window.addEventListener("mousemove", this.updateSV);
      window.addEventListener("mouseup", this.stopSVDrag);
    },
    updateSV(event) {
      if (!this.isDraggingSV) return;
      const svPanel = this.$refs.svPanel;
      if (!svPanel) return;
      const rect = svPanel.getBoundingClientRect();
      let x = ((event.clientX - rect.left) / rect.width) * 100;
      let y = ((event.clientY - rect.top) / rect.height) * 100;
      x = Math.max(0, Math.min(100, x));
      y = Math.max(0, Math.min(100, y));
      this.svPosition = { x, y };
      this.color.hsl.s = x / 100; // Saturation: 0 (left) to 1 (right)
      this.color.hsl.l = (100 - y) / 100; // Lightness: 1 (top) to 0 (bottom)
      this.color.rgb = this.hslToRgb(this.color.hsl);
      this.color.hex = this.rgbToHex(this.color.rgb);
      this.emitColor();
    },
    stopSVDrag() {
      this.isDraggingSV = false;
      window.removeEventListener("mousemove", this.updateSV);
      window.removeEventListener("mouseup", this.stopSVDrag);
    },
    startHueDrag(event) {
      if (this.disabled) return;
      this.isDraggingHue = true;
      this.updateHue(event);
      window.addEventListener("mousemove", this.updateHue);
      window.addEventListener("mouseup", this.stopHueDrag);
    },
    updateHue(event) {
      if (!this.isDraggingHue) return;
      const rect = this.$refs.hueSlider.getBoundingClientRect();
      let x = ((event.clientX - rect.left) / rect.width) * 100;
      x = Math.max(0, Math.min(100, x));
      this.huePosition = x;
      this.color.hsl.h = (x / 100) * 360;
      this.color.rgb = this.hslToRgb(this.color.hsl);
      this.color.hex = this.rgbToHex(this.color.rgb);
      this.emitColor();
    },
    stopHueDrag() {
      this.isDraggingHue = false;
      window.removeEventListener("mousemove", this.updateHue);
      window.removeEventListener("mouseup", this.stopHueDrag);
    },
    togglePicker() {
      if (!this.disabled) {
        this.showPicker = !this.showPicker;
      }
    },
    confirmSelection() {
      this.emitColor();
      this.showPicker = false;
    },
  },
  mounted() {
    this.updateColor(this.modelValue);
    this.emitColor();
    if (!this.inline) {
      this.showPicker = true;
    }
  },
};
</script>

<style lang="scss" scoped>
.color-picker {
  font-family: "Voyado Text", sans-serif;
  color: #21211f;
}

.inline-mode {
  display: inline-block;
}

.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.input-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.color-preview {
  width: 24px;
  height: 24px;
  border: 1px solid #c0c0bc;
  border-radius: 6px;
  border-radius: 4px;
  margin-left: 0.5rem;
  box-shadow: 0 0 1px rgba(0, 0, 0, 0.2);

  cursor: pointer;
}

.picker-panel {
  border: 1px solid #c0c0bc;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  background-color: #ffffff;
  padding: 1rem;
  width: 220px;
}

.popup-panel {
  position: absolute;
  z-index: 1000;
  background-color: #ffffff;
}

.sv-panel {
  position: relative;
  width: 200px;
  height: 150px;
  cursor: crosshair;
  margin-bottom: 0.5rem;
}

.sv-selector {
  position: absolute;
  width: 10px;
  height: 10px;
  border: 2px solid #fff;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
}

.hue-slider {
  position: relative;
  height: 12px;
  background: linear-gradient(
    to right,
    #ff0000 0%,
    #ffff00 17%,
    #00ff00 33%,
    #00ffff 50%,
    #0000ff 67%,
    #ff00ff 83%,
    #ff0000 100%
  );
  cursor: pointer;
  margin-bottom: 0.5rem;
}

.hue-selector {
  position: absolute;
  width: 4px;
  height: 12px;
  background: #fff;
  border: 1px solid #c0c0bc;
  transform: translateX(-50%);
  pointer-events: none;
}
</style>
